// src/utils/smartListsSaveService.js
// -----------------------------------------------------------------------------
// SmartLists canonical save orchestration service
//
// PURPOSE
//  • Provide one SmartLists-specific service entry point for canonical Save
//  • Keep spreadsheet/page orchestration OUT of managedFields.js
//  • Keep Firestore canonical writes flowing through the managedFields domain layer
//  • Reuse smartListsDraftUtils.js for deterministic normalization/save-plan building
//
// AGREED WORKFLOW
//  • local spreadsheet editing remains instant and local
//  • no separate draft/publish workflow exists for SmartLists
//  • Save writes directly to canonical org-managed docs
//  • lifecycle safety is governed by:
//      - lifecycleStatus
//      - liveFrom
//      - liveTo
//
// IMPORTANT ARCHITECTURAL RULES
//  • This file does NOT own React state
//  • This file does NOT debounce anything
//  • This file does NOT read/write browser storage
//  • This file does NOT know about canvas focus/selection behavior
//
// INPUT EXPECTATIONS
//  • organizationId
//  • userId
//  • workspaceItems                  -> current SmartLists workspace groups
//  • dirtyGroupIds                   -> optional Set<string> | string[]
//  • managedFields                   -> canonical registry snapshot from Firestore
//  • managedOptionSets               -> canonical registry snapshot from Firestore
//
// OUTPUT SHAPE
//  • {
//      status,
//      message,
//      data: {
//        savePlan,
//        diagnostics,
//        optionSetResults,
//        fieldResults,
//      },
//      error?
//    }
//
// -----------------------------------------------------------------------------

import {
  buildSmartListsCanonicalSavePlan,
} from './smartListsSaveUtils';

import {
  applySmartListOptionSetWrite,
  applySmartListManagedFieldWrite,
} from './managedFields';

import { db } from '../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';

// -----------------------------------------------------------------------------
// Internal helpers
// -----------------------------------------------------------------------------

const logPrefix = '[smartListsSaveService]';

const normalizeString = (value, fallback = '') => {
  return typeof value === 'string' ? value.trim() : fallback;
};

const assertOrganizationId = (organizationId) => {
  const normalized = normalizeString(organizationId);
  if (!normalized) {
    throw new Error(`${logPrefix} organizationId is required.`);
  }
  return normalized;
};

const assertUserId = (userId) => {
  const normalized = normalizeString(userId);
  if (!normalized) {
    throw new Error(`${logPrefix} userId is required.`);
  }
  return normalized;
};

const normalizeDirtyGroupIds = (dirtyGroupIds) => {
  if (dirtyGroupIds instanceof Set) return dirtyGroupIds;
  if (Array.isArray(dirtyGroupIds)) return new Set(dirtyGroupIds);
  return null;
};

// -----------------------------------------------------------------------------
// Public service helpers
// -----------------------------------------------------------------------------

/**
 * Build a SmartLists canonical save plan only.
 *
 * USE CASE
 *  • helpful for preview/debug/testing before actual Save
 *  • page can inspect what would be written without writing yet
 */
export const buildSmartListsSavePlan = ({
  workspaceItems = [],
  dirtyGroupIds = null,
  managedFields = [],
  managedOptionSets = [],
} = {}) => {
  const normalizedDirtyGroupIds = normalizeDirtyGroupIds(dirtyGroupIds);

  const savePlan = buildSmartListsCanonicalSavePlan({
    workspaceItems,
    dirtyGroupIds: normalizedDirtyGroupIds,
    managedFields,
    managedOptionSets,
  });

  console.log(`${logPrefix} Built SmartLists save plan successfully.`, {
    managedFieldWriteCount: Array.isArray(savePlan?.managedFieldWrites)
      ? savePlan.managedFieldWrites.length
      : 0,
    managedOptionSetWriteCount: Array.isArray(savePlan?.managedOptionSetWrites)
      ? savePlan.managedOptionSetWrites.length
      : 0,
    diagnostics: savePlan?.diagnostics || null,
  });

  return {
    status: 'success',
    message: 'SmartLists save plan built successfully.',
    data: savePlan,
  };
};

/**
 * Apply a full SmartLists canonical save.
 *
 * SAVE ORDER
 *  1) managed option sets
 *  2) managed fields
 *
 * WHY
 *  • managed fields reference optionsSource.optionsSetId
 *  • writing option sets first is the safer canonical sequence
 */
export const saveSmartListsCanonically = async ({
  organizationId,
  userId,
  workspaceItems = [],
  dirtyGroupIds = null,
  managedFields = [],
  managedOptionSets = [],
} = {}) => {
  try {
    const normalizedOrganizationId = assertOrganizationId(organizationId);
    const normalizedUserId = assertUserId(userId);
    const normalizedDirtyGroupIds = normalizeDirtyGroupIds(dirtyGroupIds);

    const savePlan = buildSmartListsCanonicalSavePlan({
      workspaceItems,
      dirtyGroupIds: normalizedDirtyGroupIds,
      managedFields,
      managedOptionSets,
    });

    const managedOptionSetWrites = Array.isArray(savePlan?.managedOptionSetWrites)
      ? savePlan.managedOptionSetWrites
      : [];

    const managedFieldWrites = Array.isArray(savePlan?.managedFieldWrites)
      ? savePlan.managedFieldWrites
      : [];

    if (managedOptionSetWrites.length === 0 && managedFieldWrites.length === 0) {
      console.log(`${logPrefix} No SmartLists canonical writes were required.`, {
        organizationId: normalizedOrganizationId,
        userId: normalizedUserId,
        diagnostics: savePlan?.diagnostics || null,
      });

      return {
        status: 'success',
        message: 'No SmartLists changes needed saving.',
        data: {
          savePlan,
          diagnostics: savePlan?.diagnostics || {},
          optionSetResults: [],
          fieldResults: [],
        },
      };
    }

    const optionSetResults = [];
    const fieldResults = [];

    // -------------------------------------------------------------------------
    // Step 1: managed option set writes
    // -------------------------------------------------------------------------
    for (const write of managedOptionSetWrites) {
      const result = await applySmartListOptionSetWrite({
        organizationId: normalizedOrganizationId,
        userId: normalizedUserId,
        optionsSetId: write?.optionsSetId,
        patch: write?.patch,
      });

      optionSetResults.push({
        localDraftId: write?.localDraftId || null,
        managedFieldId: write?.managedFieldId || null,
        optionsSetId: write?.optionsSetId || null,
        ...result,
      });
    }

    // -------------------------------------------------------------------------
    // Step 2: managed field writes
    // -------------------------------------------------------------------------
    for (const write of managedFieldWrites) {
      const result = await applySmartListManagedFieldWrite({
        organizationId: normalizedOrganizationId,
        userId: normalizedUserId,
        managedFieldId: write?.managedFieldId,
        patch: write?.patch,
      });

      fieldResults.push({
        localDraftId: write?.localDraftId || null,
        optionsSetId: write?.optionsSetId || null,
        managedFieldId: write?.managedFieldId || null,
        ...result,
      });
    }

    // -------------------------------------------------------------------------
    // Org-level invalidation signal
    // -------------------------------------------------------------------------
    // PURPOSE
    //  • Tell already-open form builders / published forms that org-managed
    //    SmartLists have changed and their runtime dropdown map should refresh.
    //
    // WHY HERE
    //  • This is the canonical SmartLists save boundary.
    //  • Emitting the signal here keeps invalidation aligned with the actual
    //    managed-field / managed-option-set writes.
    //
    // CONTRACT
    //  • Consumers can subscribe to:
    //      organizations/{orgId}.smartListsUpdatedAt
    //  • When it changes, they rerun managed dropdown resolution.
    // -------------------------------------------------------------------------
    await updateDoc(doc(db, 'organizations', normalizedOrganizationId), {
      smartListsUpdatedAt: serverTimestamp(),
    });

    console.log(`${logPrefix} SmartLists canonical save completed successfully.`, {
      organizationId: normalizedOrganizationId,
      userId: normalizedUserId,
      optionSetWriteCount: optionSetResults.length,
      fieldWriteCount: fieldResults.length,
      diagnostics: savePlan?.diagnostics || null,
    });

    return {
      status: 'success',
      message: 'SmartLists saved successfully.',
      data: {
        savePlan,
        diagnostics: savePlan?.diagnostics || {},
        optionSetResults,
        fieldResults,
      },
    };
  } catch (error) {
    console.error(`${logPrefix} SmartLists canonical save failed.`, {
      organizationId,
      userId,
      error,
    });

    return {
      status: 'error',
      message: `Failed to save SmartLists: ${error.message}`,
      error,
      data: null,
    };
  }
};